import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Star, Quote, Sparkles } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const items = [
  {
    quote:
      "Professional guidance throughout my investment journey. The team explained every step with clarity.",
    name: "Aarav M.",
    role: "SIP & Mutual Funds",
  },
  {
    quote:
      "Excellent support for my home loan process. Paperwork was smooth and communication was constant.",
    name: "Priya S.",
    role: "Home Loan Client",
  },
  {
    quote:
      "Income tax filing was quick and completely hassle-free. Genuinely felt taken care of.",
    name: "Rahul V.",
    role: "Self-employed Professional",
  },
];

function useItemsPerView() {
  const [perView, setPerView] = useState(3);
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w < 640) setPerView(1);
      else if (w < 1024) setPerView(2);
      else setPerView(3);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);
  return perView;
}

function TiltCard({ t, i }) {
  const ref = useRef(null);
  const [hovered, setHovered] = useState(false);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-60, 60], [8, -8]), {
    stiffness: 200,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(x, [-60, 60], [-8, 8]), {
    stiffness: 200,
    damping: 20,
  });
  const glowX = useTransform(x, [-60, 60], [0, 100]);
  const glowY = useTransform(y, [-60, 60], [0, 100]);

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    x.set(e.clientX - rect.left - rect.width / 2);
    y.set(e.clientY - rect.top - rect.height / 2);
  };

  const handleLeave = () => {
    x.set(0);
    y.set(0);
    setHovered(false);
  };

  return (
    <motion.figure
      ref={ref}
      onMouseMove={handleMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={handleLeave}
      style={{ rotateX, rotateY, transformPerspective: 900 }}
      data-testid={`testimonial-${i}`}
      className="group relative rounded-2xl border border-[#EFEFEA] bg-white p-8 overflow-hidden will-change-transform h-full"
    >
      <motion.div
        className="pointer-events-none absolute -inset-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: useTransform(
            [glowX, glowY],
            ([gx, gy]) =>
              `radial-gradient(180px circle at ${gx}% ${gy}%, rgba(201,162,39,0.14), transparent 70%)`
          ),
        }}
      />

      <motion.span
        className="absolute top-5 right-5 text-[#C9A227]"
        animate={
          hovered
            ? { opacity: 1, scale: 1, rotate: 180 }
            : { opacity: 0, scale: 0.4, rotate: 0 }
        }
        transition={{ duration: 0.5, ease }}
      >
        <Sparkles size={16} strokeWidth={1.5} />
      </motion.span>

      <span className="absolute top-0 left-0 h-[3px] w-0 bg-gradient-to-r from-[#C9A227] via-[#E4C767] to-[#C9A227] transition-all duration-500 group-hover:w-full" />

      <motion.div
        className="absolute -top-2 right-6 text-[#C9A227]/10 group-hover:text-[#C9A227]/20"
        animate={{ rotate: hovered ? 12 : 0, scale: hovered ? 1.08 : 1 }}
        transition={{ duration: 0.5, ease }}
      >
        <Quote size={72} strokeWidth={0} fill="currentColor" />
      </motion.div>

      <div style={{ transform: "translateZ(30px)" }}>
        <div className="relative flex gap-1 text-[#C9A227]">
          {Array.from({ length: 5 }).map((_, s) => (
            <Star key={s} size={14} fill="#C9A227" strokeWidth={0} />
          ))}
        </div>

        <blockquote className="relative mt-6 font-display text-[22px] leading-[1.35] text-[#0F172A]">
          &ldquo;{t.quote}&rdquo;
        </blockquote>

        <figcaption className="relative mt-6 pt-6 border-t border-[#F1F1EC] flex items-center gap-3">
          <motion.span
            animate={{ scale: hovered ? 1.15 : 1, rotate: hovered ? 6 : 0 }}
            transition={{ duration: 0.4, ease }}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0F172A] text-[12px] font-medium text-white"
          >
            {t.name.charAt(0)}
          </motion.span>
          <div>
            <div className="text-[14px] font-medium text-[#0F172A]">
              {t.name}
            </div>
            <div className="text-[12px] tracking-wide uppercase text-[#64748B] mt-0.5">
              {t.role}
            </div>
          </div>
        </figcaption>
      </div>
    </motion.figure>
  );
}

export default function Testimonials() {
  const perView = useItemsPerView();
  const totalGroups = Math.ceil(items.length / perView);
  const [group, setGroup] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    setGroup(0);
  }, [perView]);

  useEffect(() => {
    if (totalGroups <= 1) return;
    timerRef.current = setInterval(() => {
      setGroup((g) => (g + 1) % totalGroups);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [totalGroups]);

  const padded = [...items];
  while (padded.length % perView !== 0) padded.push(null);

  return (
    <section
      data-testid="testimonials-section"
      className="relative mx-auto max-w-7xl px-6 md:px-10 pt-10 md:pt-14 pb-10 md:pb-14 overflow-hidden"
    >
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -top-10 -left-24 h-72 w-72 rounded-full bg-[#C9A227]/[0.07] blur-3xl"
        animate={{ x: [0, 30, 0], y: [0, 20, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -bottom-16 -right-16 h-80 w-80 rounded-full bg-[#0F766E]/[0.06] blur-3xl"
        animate={{ x: [0, -24, 0], y: [0, -18, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
        <div>
          <motion.p
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease }}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase text-[#C9A227]"
          >
            <span className="relative h-1.5 w-1.5 rounded-full bg-[#C9A227]">
              <span className="absolute inset-0 rounded-full bg-[#C9A227] animate-ping" />
            </span>
            Client Voices
          </motion.p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-[42px] leading-[1.05] tracking-tight text-[#0F172A] overflow-hidden">
            <motion.span
              className="block"
              initial={{ y: "100%" }}
              whileInView={{ y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease, delay: 0.1 }}
            >
              Trusted quietly,
            </motion.span>
            <motion.span
              className="block italic"
              initial={{ y: "100%" }}
              whileInView={{ y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease, delay: 0.22 }}
            >
              recommended openly.
            </motion.span>
          </h2>
        </div>
      </div>

      <div className="relative overflow-hidden">
        <div
          className="flex transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{ transform: `translateX(-${group * 100}%)` }}
        >
          {Array.from({ length: totalGroups }).map((_, gi) => (
            <div
              key={gi}
              className="grid shrink-0 w-full gap-6"
              style={{
                gridTemplateColumns: `repeat(${perView}, minmax(0, 1fr))`,
              }}
            >
              {padded
                .slice(gi * perView, gi * perView + perView)
                .map((t, i) =>
                  t ? (
                    <TiltCard key={t.name} t={t} i={gi * perView + i} />
                  ) : (
                    <div key={`empty-${gi}-${i}`} />
                  )
                )}
            </div>
          ))}
        </div>
      </div>

      {totalGroups > 1 && (
        <div className="flex items-center justify-center gap-2 mt-10">
          {Array.from({ length: totalGroups }).map((_, gi) => (
            <button
              key={gi}
              aria-label={`Show testimonial group ${gi + 1}`}
              onClick={() => setGroup(gi)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                gi === group ? "w-6 bg-[#C9A227]" : "w-1.5 bg-[#E2E8F0]"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}